import { Api } from '@core/api';
import { injectTSDI } from '@core/tsdi';
import { FacilitySearchSimpleDto } from '@api/StudioClient';
import { Combobox } from '@components/combobox';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import React from 'react';

interface AddFacilityRestrictionDialogProps {
    open: boolean;
    onClose: () => void;
    onAdd: (facilityId: number, facilityName: string) => void;
}

export const AddFacilityRestrictionDialog: React.FC<
    AddFacilityRestrictionDialogProps
> = ({ open, onClose, onAdd }) => {
    const { studioClient } = injectTSDI(Api);

    const [options, setOptions] = React.useState<FacilitySearchSimpleDto[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [selected, setSelected] =
        React.useState<FacilitySearchSimpleDto | null>(null);
    const [searchText, setSearchText] = React.useState('');

    const searchFacilities = async (query: string) => {
        if (query.length < 2) {
            setOptions([]);
            return;
        }
        setLoading(true);
        try {
            const response =
                await studioClient.v1.closedFacilityControllerGetPage({
                    search: query,
                    status: ['VERIFIED'],
                    size: 20
                });
            setOptions(response.data.content || []);
        } catch {
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        const timer = setTimeout(() => {
            void searchFacilities(searchText);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchText]);

    const handleClose = () => {
        setSelected(null);
        setSearchText('');
        setOptions([]);
        onClose();
    };

    const getOptionLabel = (option: FacilitySearchSimpleDto) => {
        const addr = option.address;
        const addressStr = addr
            ? `${addr.street || ''}, ${addr.city || ''}`.replace(/^, |, $/, '')
            : '';
        return addressStr ? `${option.name} (${addressStr})` : option.name;
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Add Facility Restriction</DialogTitle>
                </DialogHeader>
                <Combobox<FacilitySearchSimpleDto>
                    label="Search facility"
                    placeholder="Select facility"
                    searchPlaceholder="Type at least 2 characters..."
                    options={options}
                    getOptionLabel={getOptionLabel}
                    getOptionKey={(option) => option.id}
                    loading={loading}
                    value={selected}
                    onChange={setSelected}
                    onInputChange={setSearchText}
                />
                <DialogFooter>
                    <Button variant="ghost" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        disabled={!selected}
                        onClick={() => {
                            if (selected) {
                                onAdd(selected.id, selected.name);
                            }
                        }}
                    >
                        Add Restriction
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
